/**
 * Call-site URL / referer sanitizers for the redaction layer.
 *
 * These back-stop the generic redactor for the two High-severity leakage points
 * surfaced during review, where generic scrubbing cannot infer that a
 * value is sensitive:
 *   1. SSR `resolvedUrl` — dynamic path segments (e.g. `/account/email-change/<token>`)
 *      look like ordinary path text, so they are redacted using the route's
 *      `context.params`; the query string is stripped entirely.
 *   2. `referer` header — dropped to safe signal only.
 */

import type { ParsedUrlQuery } from 'node:querystring';

import { SENSITIVE_PATH_PARENT_SEGMENTS } from './patterns';

/**
 * Strips the query string (and fragment) from an SSR `resolvedUrl` and replaces
 * any dynamic path segment whose decoded value matches a `context.params` value
 * with a `[paramKey]` placeholder.
 *
 * Example: `('/account/email-change/abc123?email=x&code=y', { token: 'abc123' })`
 * → `'/account/email-change/[token]'`.
 *
 * Handles string params and string-array (catch-all `[...slug]`) params. Falls
 * back to a query-stripped path when `params` is absent.
 */
export const sanitizeServerSidePath = (resolvedUrl: string, params?: ParsedUrlQuery): string => {
    const pathOnly = resolvedUrl.split(/[?#]/)[0] ?? resolvedUrl;

    if (!params) return pathOnly;

    // Map each sensitive param value to the placeholder it should become.
    const valueToPlaceholder = new Map<string, string>();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined) continue;
        const values = Array.isArray(value) ? value : [value];
        for (const single of values) {
            if (single) valueToPlaceholder.set(single, `[${key}]`);
        }
    }

    if (valueToPlaceholder.size === 0) return pathOnly;

    return pathOnly
        .split('/')
        .map((segment) => {
            if (!segment) return segment;
            let decoded = segment;
            try {
                decoded = decodeURIComponent(segment);
            } catch {
                // Malformed escape sequence — compare against the raw segment.
            }
            return valueToPlaceholder.get(decoded) ?? valueToPlaceholder.get(segment) ?? segment;
        })
        .join('/');
};

const DYNAMIC_SEGMENT_PLACEHOLDER = '[id]';
const VERSION_SEGMENT_REGEX = /^v\d+(\.\d+)*$/i;
const JSON_EXTENSION_REGEX = /\.json$/i;
const MIN_OPAQUE_SEGMENT_LENGTH = 32;

// Upstream paths embed IDs and secrets (e.g. `logoutdevices/<token>.json`) that the generic
// redactor cannot recognise, so any segment that looks like data rather than a route word is masked.
const isDynamicSegment = (stem: string): boolean =>
    !VERSION_SEGMENT_REGEX.test(stem) &&
    (/\d/.test(stem) || stem.includes('@') || stem.length >= MIN_OPAQUE_SEGMENT_LENGTH);

const maskSegment = (segment: string, previous: string | undefined): string => {
    const extension = JSON_EXTENSION_REGEX.exec(segment)?.[0] ?? '';
    const stem = segment.slice(0, segment.length - extension.length);
    const isSecretSlot = SENSITIVE_PATH_PARENT_SEGMENTS.has(previous?.toLowerCase() ?? '');
    return stem && (isSecretSlot || isDynamicSegment(stem))
        ? `${DYNAMIC_SEGMENT_PLACEHOLDER}${extension}`
        : segment;
};

/**
 * Reduces an upstream API URL (absolute or path-only) to a route-like pathname: host, query
 * string, and fragment are dropped, and ID/token-like segments, plus any segment following a
 * `SENSITIVE_PATH_PARENT_SEGMENTS` entry, become `[id]`. Returns `undefined` when the value is
 * not a parseable string.
 *
 * Example: `'http://api-cache/api/shows/970006178/contentfeed.json?term=x'`
 * → `'/api/shows/[id]/contentfeed.json'`.
 */
export const sanitizeUpstreamUrl = (url: unknown): string | undefined => {
    if (typeof url !== 'string' || url === '') return undefined;

    try {
        return new URL(url, 'http://upstream.invalid').pathname
            .split('/')
            .map((segment, index, segments) => maskSegment(segment, segments[index - 1]))
            .join('/');
    } catch {
        return undefined;
    }
};

const EMBEDDED_URL_REGEX = /https?:\/\/[^\s'"]+/gi;
const UNPARSEABLE_URL_PLACEHOLDER = '[url]';

const sanitizeEmbeddedUrl = (url: string): string => {
    try {
        return `${new URL(url).origin}${sanitizeUpstreamUrl(url) ?? ''}`;
    } catch {
        return UNPARSEABLE_URL_PLACEHOLDER;
    }
};

/**
 * Reduces every absolute URL embedded in free text, e.g. a Node fetch failure message
 * (`request to <url> failed, reason: …`), to its origin plus the `sanitizeUpstreamUrl` pathname.
 *
 * Example: `'request to http://api-cache/api/v3.0/useraccount/logoutdevices/abc.json?x=1 failed'`
 * → `'request to http://api-cache/api/v3.0/useraccount/logoutdevices/[id].json failed'`.
 */
export const sanitizeUrlsInText = (text: string): string =>
    text.replace(EMBEDDED_URL_REGEX, sanitizeEmbeddedUrl);

/**
 * `EMBEDDED_URL_REGEX` anchored: an unmatched remainder would reach the log verbatim.
 * Keep it non-global — `/g` would make `test` stateful.
 */
const WHOLE_ABSOLUTE_URL_REGEX = /^https?:\/\/[^\s'"]+$/i;

/**
 * Reduces a standalone URL to its origin plus the `sanitizeUpstreamUrl` pathname; anything that is
 * not a whole absolute URL becomes `[url]`.
 *
 * Example: `'https://s.vendor.net/ad/l/1?imp=abc123'` → `'https://s.vendor.net/ad/l/[id]'`.
 */
export const sanitizeUrlEndpoint = (url: string): string =>
    WHOLE_ABSOLUTE_URL_REGEX.test(url) ? sanitizeUrlsInText(url) : UNPARSEABLE_URL_PLACEHOLDER;

/** Safe signal derived from a referer header — never the raw value. */
export type SanitizedReferer = {
    hasReferer: boolean;
    /** Scheme + host only, present only when the referer parses as a URL. */
    origin?: string;
};

/**
 * Reduces a raw `referer` header to safe signal: whether one was present, plus
 * the origin (scheme + host) when it parses as a URL. The full value — which can
 * carry auth codes, emails, or session tokens in its path/query — is dropped.
 */
export const sanitizeReferer = (referer?: string): SanitizedReferer => {
    if (!referer) return { hasReferer: false };

    try {
        return { hasReferer: true, origin: new URL(referer).origin };
    } catch {
        return { hasReferer: true };
    }
};
